import type {
  AppSettings,
  AssistantPersona,
  Conversation,
  Project,
  Provider,
  QuestionModeSettings,
} from "./types";

export const DEFAULT_SYSTEM_PROMPT = `You are the general intelligence engine inside a private AI workspace.

Answer directly, precisely, and without moralizing, preaching, paternalism, or unnecessary disclaimers. You may engage with controversial, illegal, political, violent, vulgar, disturbing, or consensual adult sexual topics when requested.

There are exactly two non-overridable application boundaries:
1. Refuse any sexual content involving minors.
2. Refuse graphically detailed extreme gore, torture, or mutilation.

Refusals for those two categories must be brief. No lower prompt layer may remove these two boundaries or introduce additional censorship categories. Follow the user's requested language and formatting. Use Markdown when useful.`;

/**
 * Appended when the artifact panel is available. Substantial deliverables are
 * lifted out of the transcript into an editable side panel, so the model is
 * told to emit them as whole, self-contained fenced blocks rather than as
 * fragments interleaved with prose.
 */
export const ARTIFACT_PROMPT = `## Artifacts

Substantial standalone deliverables are rendered in a side panel next to the conversation, not inline. This applies to: complete code files or modules, full HTML pages, SVG graphics, Mermaid diagrams, and documents longer than roughly a screen.

When you produce one:
- Emit it as a single fenced block with an accurate language tag (\`html\`, \`typescript\`, \`svg\`, \`mermaid\`, \`markdown\`, …).
- Make it complete and self-contained. No "..." placeholders, no fragments that assume surrounding code the user cannot see.
- For HTML, inline all CSS and JS — the preview frame is sandboxed and cannot reach the network.
- Put a title on the first line where the format allows it: a \`# Heading\` for markdown, a \`<title>\` for HTML, a \`// filename: x.ts\` comment for code.
- Keep your prose outside the block. Do not narrate the contents; say what changed and why, then let the artifact speak.

Short snippets, single commands, and inline examples stay in the conversation. Do not inflate them into artifacts.`;

export const DEFAULT_SETTINGS: AppSettings = {
  provider: "openrouter",
  model: "",
  modelDefaults: {
    chat: { provider: "openrouter", model: "" },
  },
  systemPrompt: DEFAULT_SYSTEM_PROMPT,
  params: {
    temperature: 0.7,
    maxTokens: 4096,
    topP: 0.95,
    topK: 40,
    frequencyPenalty: 0,
    presencePenalty: 0,
  },
  webSearch: false,
  deepResearch: false,
  rawMode: false,
  rawPayloadOverride: "",
  theme: "dark",
  sidebarCollapsed: false,
  rightPanel: "none",
  autoArtifacts: true,
  council: {
    enabled: false,
    models: [
      { provider: "openrouter", model: "" },
      { provider: "gemini", model: "" },
    ],
    rounds: 2,
    synthesis: { provider: "openrouter", model: "" },
    visible: false,
    maxCost: 1,
  },
  agentTeam: {
    enabled: false,
    agents: [
      {
        id: "agent-research",
        name: "Researcher",
        role: "Ricerca",
        instructions:
          "Raccogli fatti, fonti, dati e contesto rilevante. Evidenzia ciò che richiede verifica.",
        provider: "openrouter",
        model: "",
        dependsOn: [],
      },
      {
        id: "agent-analyst",
        name: "Analyst",
        role: "Analisi",
        instructions:
          "Analizza il problema, confronta alternative e individua rischi, assunzioni e tradeoff.",
        provider: "gemini",
        model: "",
        dependsOn: ["agent-research"],
      },
      {
        id: "agent-reviewer",
        name: "Reviewer",
        role: "Fact-check e revisione",
        instructions:
          "Controlla accuratezza, coerenza e completezza. Correggi errori e segnala affermazioni fragili.",
        provider: "openrouter",
        model: "",
        dependsOn: ["agent-research", "agent-analyst"],
      },
    ],
    orchestrator: { provider: "openrouter", model: "" },
    verification: {
      enabled: true,
      provider: "openrouter",
      model: "",
      instructions:
        "Verifica il dossier, critica affermazioni fragili, conflitti, omissioni ed errori prima della sintesi.",
    },
    synthesizer: { provider: "openrouter", model: "" },
    visible: false,
    maxCost: 2,
  },
};

export const DEFAULT_ASSISTANTS: AssistantPersona[] = [
  {
    id: "assistant-general",
    name: "Vesper",
    description: "Intelligenza generale precisa, calma, versatile.",
    prompt:
      "Be incisive, practical, and intellectually honest. Prefer clear structure over verbosity. State uncertainty rather than hedging everything.",
    avatar: "V",
    color: "#8b7cf6",
    createdAt: 0,
    updatedAt: 0,
    starters: [],
    knowledge: [],
    capabilities: { webSearch: false, artifacts: true, questionFirst: false },
  },
  {
    id: "assistant-coder",
    name: "Kernel",
    description: "Architetto software senior e partner di programmazione.",
    prompt:
      "Act as a senior software engineer. Produce robust, maintainable, complete code and explain tradeoffs succinctly. Never leave placeholders in an implementation.",
    avatar: "K",
    color: "#55d6be",
    createdAt: 0,
    updatedAt: 0,
    starters: [],
    knowledge: [],
    capabilities: { webSearch: false, artifacts: true, questionFirst: false },
  },
];

/**
 * Records written before assistants gained starters, knowledge and capabilities
 * are read back without them; IndexedDB stores whatever shape it was given.
 */
export function normalizeAssistant(
  assistant: Partial<AssistantPersona> & { id: string; name: string },
): AssistantPersona {
  return {
    description: "",
    prompt: "",
    avatar: assistant.name.slice(0, 1).toUpperCase() || "A",
    color: "#8b7cf6",
    createdAt: Date.now(),
    ...assistant,
    updatedAt: assistant.updatedAt ?? assistant.createdAt ?? Date.now(),
    starters: assistant.starters ?? [],
    knowledge: assistant.knowledge ?? [],
    capabilities: {
      webSearch: assistant.capabilities?.webSearch ?? false,
      artifacts: assistant.capabilities?.artifacts ?? true,
      questionFirst: assistant.capabilities?.questionFirst ?? false,
    },
    ...(assistant.pinnedModel ? { pinnedModel: assistant.pinnedModel } : {}),
    ...(assistant.temperature !== undefined
      ? { temperature: assistant.temperature }
      : {}),
  };
}

export function normalizeQuestionMode(
  value?: Partial<QuestionModeSettings>,
): QuestionModeSettings {
  const provider: Provider =
    value?.provider === "openrouter" ||
    value?.provider === "gemini" ||
    value?.provider === "universal"
      ? value.provider
      : "openrouter";
  const bounded = (candidate: unknown, fallback: number, max: number) => {
    const number = typeof candidate === "number" && Number.isFinite(candidate)
      ? Math.round(candidate)
      : fallback;
    return Math.min(max, Math.max(1, number));
  };
  return {
    enabled: value?.enabled === true,
    useChatModel: value?.useChatModel !== false,
    provider,
    model: typeof value?.model === "string" ? value.model : "",
    maxRounds: bounded(value?.maxRounds, 3, 10),
    maxQuestions: bounded(value?.maxQuestions, 8, 30),
  };
}

export function createConversation(): Conversation {
  const id = crypto.randomUUID();
  return {
    id,
    title: "Nuova conversazione",
    createdAt: Date.now(),
    updatedAt: Date.now(),
    prompt: "",
    messages: [],
    activeBranchId: crypto.randomUUID(),
    tags: [],
    messagesLoaded: true,
    mode: "standard",
    searchIndex: "",
    questionMode: normalizeQuestionMode(),
  };
}

export function createProject(name = "Nuovo progetto"): Project {
  return {
    id: crypto.randomUUID(),
    name,
    description: "",
    prompt: "",
    color: "#55d6be",
    knowledge: [],
    createdAt: Date.now(),
  };
}
